<template>
  <div class="admin-dashboard">
    <section class="admin-dashboard__hero mb-6">
      <div>
        <div class="text-overline font-weight-bold" :class="primaryTextClass">Business overview</div>
        <h1 class="text-h5 text-sm-h4 font-weight-bold mb-1">Welcome back</h1>
        <p class="text-body-2 admin-muted mb-0">{{ todayLabel }}</p>
      </div>
    </section>

    <v-alert
      v-if="isNativeApp"
      :type="notificationAlertType"
      :outlined="notificationStatus !== 'enabled'"
      class="rounded-xl mb-6"
    >
      <div class="d-flex align-center flex-wrap">
        <div class="flex-grow-1 pr-3">
          <div class="font-weight-bold">{{ notificationTitle }}</div>
          <div class="text-body-2">{{ notificationMessage }}</div>
        </div>
        <v-btn
          v-if="notificationStatus !== 'enabled' && notificationStatus !== 'unavailable'"
          color="primary"
          depressed
          rounded
          class="mt-3 mt-sm-0"
          :loading="enablingNotifications"
          @click="enableNotifications"
        >
          {{ notificationStatus === 'denied' ? 'Try again' : 'Enable notifications' }}
        </v-btn>
      </div>
    </v-alert>

    <section class="overview-toolbar mb-4" aria-label="Reporting period">
      <div>
        <h2 class="text-h6 font-weight-bold">{{ periodTitle }}</h2>
        <p class="text-caption admin-muted mb-0">{{ periodLabel }} · {{ timezone }} · All locations</p>
      </div>
      <div class="overview-toolbar__controls">
        <v-select
          v-model="period"
          :items="periods"
          label="Period"
          aria-label="Reporting period"
          dense
          outlined
          hide-details
          class="overview-period"
          @change="loadOverview"
        />
        <v-btn icon :color="actionColor" min-width="44" min-height="44" :disabled="isLoading" aria-label="Refresh business overview" @click="loadOverview">
          <v-icon>mdi-refresh</v-icon>
        </v-btn>
      </div>
    </section>

    <v-alert v-if="errorMessage" type="error" text class="rounded-lg" role="alert">
      <div class="d-flex flex-wrap align-center justify-space-between">
        <span class="text--primary">{{ errorMessage }}</span>
        <v-btn text :color="$vuetify.theme.dark ? actionColor : 'primary darken-1'" min-height="44" @click="loadOverview">Try again</v-btn>
      </div>
    </v-alert>

    <div v-else-if="isLoading && !overview" role="status" aria-live="polite" aria-label="Loading business overview">
      <v-row class="mb-4">
        <v-col v-for="index in 3" :key="index" cols="12" sm="4"><v-skeleton-loader type="article" /></v-col>
      </v-row>
      <v-skeleton-loader type="table-heading, list-item-two-line, list-item-two-line" />
    </div>

    <template v-else-if="overview">
      <template v-if="overview.locations.length">
        <v-alert v-if="overview.mixedCurrencies" type="info" :color="actionColor" text dense class="rounded-lg mb-4">
          <span class="text--primary">These locations use different currencies. Review each location below; a combined sales total is not shown.</span>
        </v-alert>
        <v-row v-else-if="overview.totals" class="mb-4" dense>
          <v-col cols="12" sm="4">
            <v-card outlined class="rounded-xl pa-4 pa-sm-5" height="100%">
              <div class="text-body-2 admin-muted">Total sale</div>
              <div class="overview-metric font-weight-bold my-1" :class="primaryTextClass">{{ overview.totals.totalSale | currency(overview.totals.currencySymbol) }}</div>
              <div class="text-caption admin-muted">Across {{ overview.locations.length }} {{ overview.locations.length === 1 ? 'location' : 'locations' }}</div>
            </v-card>
          </v-col>
          <v-col cols="6" sm="4">
            <v-card outlined class="rounded-xl pa-4 pa-sm-5" height="100%">
              <div class="text-body-2 admin-muted">Invoices</div>
              <div class="overview-metric font-weight-bold my-1">{{ overview.totals.invoiceCount.toLocaleString() }}</div>
              <div class="text-caption admin-muted">Across all selected shifts</div>
            </v-card>
          </v-col>
          <v-col cols="6" sm="4">
            <v-card outlined class="rounded-xl pa-4 pa-sm-5" height="100%">
              <div class="text-body-2 admin-muted">Average sale</div>
              <div class="overview-metric font-weight-bold my-1"><template v-if="overview.totals.invoiceCount">{{ overview.totals.averageSale | currency(overview.totals.currencySymbol) }}</template><template v-else>—</template></div>
              <div class="text-caption admin-muted">{{ overview.totals.invoiceCount ? 'Total sale per invoice' : 'No invoices in selected shifts' }}</div>
            </v-card>
          </v-col>
        </v-row>

        <v-card outlined class="admin-panel rounded-xl">
          <v-card-title class="overview-location-header pa-4 pa-sm-5">
            <div>
              <h2 class="text-h6 font-weight-bold">Sales by location</h2>
              <p class="text-caption admin-muted font-weight-regular mb-0">Click any location for more details.</p>
            </div>
            <v-text-field
              v-model="locationSearch"
              aria-label="Search locations"
              autocomplete="off"
              clearable
              :color="actionColor"
              dense
              hide-details
              outlined
              placeholder="Search locations"
              prepend-inner-icon="mdi-magnify"
              class="overview-search"
            />
          </v-card-title>
          <v-divider />
          <div v-if="filteredLocations.length" role="list" aria-label="Location sales">
            <div v-for="(location, index) in filteredLocations" :key="location.id" role="listitem">
              <v-divider v-if="index" />
              <button type="button" class="overview-location-button pa-4 pa-sm-5" aria-haspopup="dialog"
                :aria-labelledby="`location-name-${location.id} location-total-${location.id}`"
                :aria-describedby="`location-breakdown-${location.id}`"
                @click="openSaleCheck(location, $event)">
                <span class="overview-location-heading">
                  <span class="min-width-0">
                    <span :id="`location-name-${location.id}`" class="d-block text-subtitle-2 font-weight-bold overview-location-name"><span v-for="(part, partIndex) in highlightParts(location.name)" :key="partIndex" :class="part.match ? [primaryTextClass, 'overview-match'] : []">{{ part.text }}</span></span>
                    <span v-if="!location.isActive" class="d-block text-caption admin-muted">Inactive location</span>
                    <span v-else-if="!location.shiftCount" class="d-block text-caption admin-muted">No shifts this period</span>
                  </span>
                  <span :id="`location-total-${location.id}`" class="overview-location-total">
                    <span class="d-block text-caption admin-muted">Total sale</span>
                    <strong class="text-body-1 font-weight-bold">{{ location.totalSale | currency(location.currencySymbol) }}</strong>
                  </span>
                </span>
                <span :id="`location-breakdown-${location.id}`" class="overview-location-breakdown text-caption mt-2">
                  <span v-if="location.aggregateOnly" class="admin-muted">Detailed breakdown unavailable for one or more shifts.</span>
                  <template v-else><span v-for="row in location.breakdownRows" :key="row.key"><span class="admin-muted">{{ row.label }}</span> <strong class="overview-location-money font-weight-medium">{{ row.value | currency(location.currencySymbol) }}</strong></span></template>
                </span>
              </button>
            </div>
          </div>
          <div v-else class="py-10 px-4 text-center">
            <v-icon :color="actionColor" size="36">mdi-store-search-outline</v-icon>
            <h3 class="text-subtitle-1 font-weight-bold mt-2">No matching locations</h3>
            <p class="text-body-2 admin-muted mb-1">Try another location name. Totals above include every location.</p>
            <v-btn text :color="actionColor" min-height="44" @click="locationSearch = ''">Clear search</v-btn>
          </div>
          <v-divider />
          <v-card-text class="text-caption admin-muted pa-4 pa-sm-5 overview-footnote">
            <span>All registers · Full shifts on the selected business dates. Includes tax and payment activity.</span>
            <span role="status">{{ isLoading ? 'Refreshing…' : 'Updated ' + updatedLabel }}</span>
          </v-card-text>
        </v-card>
      </template>
      <v-card v-else outlined class="rounded-xl py-12 px-4 text-center">
        <v-icon size="44" :color="actionColor">mdi-store-plus-outline</v-icon>
        <h2 class="text-h6 mt-3">No locations yet</h2>
        <p class="admin-muted mb-0">Open Locations from the navigation to add your first location and start seeing its business activity here.</p>
      </v-card>
    </template>

    <v-card outlined class="admin-panel rounded-xl mt-6">
      <v-card-title class="projection-header pa-4 pa-sm-5">
        <h2 class="text-h6 font-weight-bold">Monthly projection</h2>
        <v-btn icon :color="actionColor" min-width="44" min-height="44" :disabled="projectionLoading" aria-label="Refresh monthly projections" @click="loadProjections"><v-icon>mdi-refresh</v-icon></v-btn>
        <p class="projection-context text-caption admin-muted font-weight-regular mb-0">{{ projectionLabel }} · {{ timezone }}</p>
      </v-card-title>
      <v-divider />
      <v-card-text class="pa-4 pa-sm-5">
        <v-skeleton-loader v-if="projectionLoading && !projections" type="list-item-two-line@3" aria-label="Loading monthly projections" />
        <v-alert v-else-if="projectionError" type="error" text class="rounded-lg mb-0" role="alert">
          <span class="text--primary">{{ projectionError }}</span>
          <v-btn text :color="$vuetify.theme.dark ? actionColor : 'primary darken-1'" min-height="44" @click="loadProjections">Try again</v-btn>
        </v-alert>
        <template v-else-if="projections && projections.locations.length">
          <v-row dense>
            <v-col v-for="location in projectionLocations" :key="location.id" cols="12" md="6">
              <v-card outlined class="rounded-lg pa-4" height="100%">
                <h3 class="text-subtitle-2 font-weight-bold overview-location-name mb-3">{{ location.name }}</h3>
                <div class="projection-values">
                  <div>
                    <div class="text-caption admin-muted">Revenue so far</div>
                    <div class="projection-amount font-weight-bold">{{ location.currentRevenue | currency(location.currencySymbol) }}</div>
                    <div class="text-caption admin-muted">{{ location.currentReceipts.toLocaleString() }} receipts</div>
                  </div>
                  <div>
                    <div class="text-caption admin-muted">Projected month-end</div>
                    <div class="projection-amount font-weight-bold" :class="location.outlook.textClass"><v-icon small :color="location.outlook.color" aria-hidden="true" class="mr-1">{{ location.outlook.icon }}</v-icon>{{ location.projectedRevenue | currency(location.currencySymbol) }}</div>
                    <div class="text-caption admin-muted">{{ location.projectedReceipts.toLocaleString() }} projected receipts</div>
                    <div class="text-caption" :class="location.outlook.textClass">{{ location.outlook.label }} full last month</div>
                  </div>
                </div>
                <v-divider class="my-3" />
                <div class="projection-values">
                  <div>
                    <div class="text-caption admin-muted">Same day last month</div>
                    <div class="text-body-2 font-weight-medium">{{ location.lastMonthSoFar | currency(location.currencySymbol) }}</div>
                    <div class="text-caption admin-muted">{{ location.lastMonthReceiptsSoFar.toLocaleString() }} receipts</div>
                  </div>
                  <div>
                    <div class="text-caption admin-muted">Full last month</div>
                    <div class="text-body-2 font-weight-medium">{{ location.lastMonthTotal | currency(location.currencySymbol) }}</div>
                    <div class="text-caption admin-muted">{{ location.lastMonthReceiptsTotal.toLocaleString() }} receipts</div>
                  </div>
                </div>
                <v-alert role="note" text dense :color="location.pace.color" :icon="false" class="rounded-lg text-caption font-weight-medium pa-2 mt-3 mb-0" :class="location.pace.textClass">
                  <div class="d-flex align-center"><v-icon small :color="location.pace.color" aria-hidden="true" class="mr-2">{{ location.pace.icon }}</v-icon><span>{{ projectionPace(location) }}</span></div>
                </v-alert>
              </v-card>
            </v-col>
          </v-row>
          <p class="text-caption admin-muted mt-3 mb-0">Revenue follows the terminal report and includes tax. Estimates use the daily average so far.</p>
        </template>
        <p v-else class="text-body-2 admin-muted mb-0">Add a location to see monthly projections.</p>
      </v-card-text>
    </v-card>
    <SaleCheckDialog :value="saleCheckOpen" :location="saleCheckLocation" :date-range="dateRange" :timezone="timezone" @input="setSaleCheckOpen" />
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import reportService, { saleBreakdownRows } from "@/modules/Reports/service";
import SaleCheckDialog from "@/modules/Reports/components/SaleCheckDialog.vue";
import { isNativeMobileApp } from "@/mobile/runtime";
import { enableMobileNotifications, getMobileNotificationStatus } from "@/mobile/service";

export default {
  name: "Home",
  components: { SaleCheckDialog },
  data() {
    const configuredTimezone = localStorage.getItem(`${process.env.VUE_APP_STORAGE_PREFIX}timezone`);
    const timezone = this.$moment.tz.zone(configuredTimezone || "") ? configuredTimezone : this.$moment.tz.guess();
    const day = this.$moment().tz(timezone).format("YYYY-MM-DD");
    return {
      overview: null,
      overviewOrganizationId: null,
      isLoading: true,
      errorMessage: "",
      requestId: 0,
      locationSearch: "",
      saleCheckOpen: false,
      saleCheckLocation: null,
      projections: null,
      projectionOrganizationId: null,
      projectionLoading: true,
      projectionError: "",
      projectionRequestId: 0,
      period: "today",
      timezone,
      currentDate: day,
      dateRange: { start: day, end: day },
      periods: [
        { text: "Today", value: "today" },
        { text: "Yesterday", value: "yesterday" },
        { text: "Last 7 days", value: "last7Days" },
        { text: "This month", value: "thisMonth" },
        { text: "Last month", value: "lastMonth" },
      ],
      notificationStatus: "prompt",
      enablingNotifications: false,
    };
  },
  computed: {
    ...mapGetters("global", ["currentOrganization"]),
    primaryTextClass() {
      return this.$vuetify.theme.dark ? "primary--text text--lighten-4" : "primary--text";
    },
    actionColor() {
      return this.$vuetify.theme.dark ? "primary lighten-4" : "primary";
    },
    isNativeApp() { return isNativeMobileApp; },
    todayLabel() {
      return this.$moment.tz(this.currentDate, this.timezone).format("dddd, MMMM D, YYYY");
    },
    periodTitle() {
      return { today: "Today's performance", yesterday: "Yesterday's performance", last7Days: "Last 7 days", thisMonth: "This month's performance", lastMonth: "Last month's performance" }[this.period];
    },
    periodLabel() {
      const start = this.$moment.tz(this.dateRange.start, this.timezone).format("MMM D, YYYY");
      const end = this.$moment.tz(this.dateRange.end, this.timezone).format("MMM D, YYYY");
      return start === end ? start : `${start} – ${end}`;
    },
    projectionLabel() {
      if (!this.projections) return `${this.$moment().tz(this.timezone).format("MMMM YYYY")} · This month`;
      return `${this.$moment(this.projections.month, "YYYY-MM").format("MMMM YYYY")} · As of ${this.$moment(this.projections.asOf, "YYYY-MM-DD").format("MMM D")}`;
    },
    projectionLocations() {
      return this.projections ? this.projections.locations.map((location) => ({
        ...location,
        pace: this.projectionTrend(location.currentRevenue, location.lastMonthSoFar),
        outlook: this.projectionTrend(location.projectedRevenue, location.lastMonthTotal),
      })) : [];
    },
    updatedLabel() {
      return this.overview ? this.$moment(this.overview.generatedAt).tz(this.timezone).format("h:mm A") : "";
    },
    filteredLocations() {
      const query = String(this.locationSearch || "").trim().toLowerCase();
      const locations = this.overview ? this.overview.locations : [];
      return locations.filter((location) => !query || String(location.name).toLowerCase().includes(query))
        .map((location) => ({ ...location, breakdownRows: saleBreakdownRows(location.SaleBreakdown, location.aggregateOnly) }));
    },
    notificationAlertType() {
      if (this.notificationStatus === "enabled") return "success";
      if (this.notificationStatus === "error") return "error";
      if (this.notificationStatus === "unavailable") return "warning";
      return "info";
    },
    notificationTitle() {
      return this.notificationStatus === "enabled" ? "Notifications are enabled" : "Stay up to date";
    },
    notificationMessage() {
      if (this.notificationStatus === "enabled") return "This phone is ready to receive store alerts.";
      if (this.notificationStatus === "denied") return "Notifications are off. Allow them in your phone settings, then try again.";
      if (this.notificationStatus === "unavailable") return "Notifications are not available in this test version yet. You can continue using the app normally.";
      if (this.notificationStatus === "error") return "Notifications could not be enabled. Check your connection and try again.";
      return "Enable alerts so important store activity can reach this phone.";
    },
  },
  created() {
    this.loadOverview();
    this.loadProjections();
    if (this.isNativeApp) this.loadNotificationStatus();
  },
  mounted() {
    window.addEventListener("focus", this.refreshHome);
    document.addEventListener("visibilitychange", this.refreshHome);
  },
  beforeDestroy() {
    this.requestId += 1;
    this.projectionRequestId += 1;
    window.removeEventListener("focus", this.refreshHome);
    document.removeEventListener("visibilitychange", this.refreshHome);
  },
  watch: {
    "currentOrganization.id"() { this.loadOverview(); this.loadProjections(); },
  },
  methods: {
    async loadNotificationStatus() {
      try {
        this.notificationStatus = await getMobileNotificationStatus();
      } catch (error) {
        this.notificationStatus = "error";
      }
    },
    async enableNotifications() {
      this.enablingNotifications = true;
      try {
        this.notificationStatus = await enableMobileNotifications();
      } catch (error) {
        this.notificationStatus = "error";
      } finally {
        this.enablingNotifications = false;
      }
    },
    periodRange(period) {
      const end = this.$moment().tz(this.timezone).startOf("day");
      const start = end.clone();
      if (period === "yesterday") { start.subtract(1, "day"); end.subtract(1, "day"); }
      if (period === "last7Days") start.subtract(6, "days");
      if (period === "thisMonth") start.startOf("month");
      if (period === "lastMonth") { start.subtract(1, "month").startOf("month"); end.subtract(1, "month").endOf("month"); }
      return { start: start.format("YYYY-MM-DD"), end: end.format("YYYY-MM-DD") };
    },
    async loadOverview({ background = false } = {}) {
      const requestId = ++this.requestId;
      const organizationId = this.currentOrganization && this.currentOrganization.id;
      const range = this.periodRange(this.period);
      const keepOverview = background && this.overview && this.overviewOrganizationId === organizationId && this.dateRange.start === range.start && this.dateRange.end === range.end;
      if (!background) { this.saleCheckOpen = false; this.saleCheckLocation = null; }
      this.dateRange = range;
      this.currentDate = this.$moment().tz(this.timezone).format("YYYY-MM-DD");
      this.isLoading = true;
      this.errorMessage = "";
      if (!keepOverview) this.overview = null;
      try {
        const response = await reportService.Sales.getSaleCheckOverview({ ...this.dateRange, timezone: this.timezone });
        if (requestId !== this.requestId) return;
        const data = response && response.data;
        const fields = ["totalSale", "invoiceCount", "averageSale"];
        if (!data || !Array.isArray(data.locations) ||
          data.locations.some((location) => fields.some((field) => !Number.isFinite(location[field])) || !Number.isFinite(location.shiftCount) ||
            (!location.aggregateOnly && (!location.SaleBreakdown || !Number.isFinite(location.SaleBreakdown.regularSale) || !Number.isFinite(location.SaleBreakdown.billPayment)))) ||
          (!data.mixedCurrencies && data.locations.length && (!data.totals || fields.some((field) => !Number.isFinite(data.totals[field]))))) throw new Error("Invalid overview");
        this.overview = data;
        this.overviewOrganizationId = organizationId;
      } catch (error) {
        if (requestId !== this.requestId) return;
        this.overview = null;
        this.errorMessage = "Business activity could not be loaded for this period. Please try again.";
      } finally {
        if (requestId === this.requestId) this.isLoading = false;
      }
    },
    highlightParts(value) {
      const text = String(value || "");
      const query = String(this.locationSearch || "").trim().toLowerCase();
      if (!query) return [{ text, match: false }];
      const parts = [];
      let offset = 0;
      let matchIndex = text.toLowerCase().indexOf(query);
      while (matchIndex !== -1) {
        if (matchIndex > offset) parts.push({ text: text.slice(offset, matchIndex), match: false });
        parts.push({ text: text.slice(matchIndex, matchIndex + query.length), match: true });
        offset = matchIndex + query.length;
        matchIndex = text.toLowerCase().indexOf(query, offset);
      }
      if (offset < text.length) parts.push({ text: text.slice(offset), match: false });
      return parts;
    },
    openSaleCheck(location, event) {
      if (event && event.currentTarget) event.currentTarget.focus();
      this.saleCheckLocation = location;
      this.saleCheckOpen = true;
    },
    setSaleCheckOpen(open) {
      this.saleCheckOpen = open;
      if (!open) return this.refreshHome({ force: true });
    },
    refreshHome({ force = false } = {}) {
      if (document.visibilityState !== "visible" || this.saleCheckOpen || (!force && (this.isLoading || this.projectionLoading))) return;
      return Promise.all([this.loadOverview({ background: true }), this.loadProjections({ background: true })]);
    },
    async loadProjections({ background = false } = {}) {
      const requestId = ++this.projectionRequestId;
      const organizationId = this.currentOrganization && this.currentOrganization.id;
      const month = this.$moment().tz(this.timezone).format("YYYY-MM");
      this.projectionLoading = true;
      this.projectionError = "";
      if (!background || this.projectionOrganizationId !== organizationId || (this.projections && this.projections.month !== month)) this.projections = null;
      try {
        const response = await reportService.Sales.getMonthlyProjection({ timezone: this.timezone });
        if (requestId !== this.projectionRequestId) return;
        const data = response && response.data;
        const fields = ["currentRevenue", "currentReceipts", "projectedRevenue", "projectedReceipts", "lastMonthSoFar", "lastMonthTotal", "lastMonthReceiptsSoFar", "lastMonthReceiptsTotal", "daysPassed", "totalDays"];
        if (!data || !data.month || !data.asOf || !Array.isArray(data.locations) || data.locations.some((location) => fields.some((field) => !Number.isFinite(location[field])))) throw new Error("Invalid projection");
        this.projections = data;
        this.projectionOrganizationId = organizationId;
      } catch (error) {
        if (requestId === this.projectionRequestId) {
          this.projections = null;
          this.projectionError = "Monthly projections could not be loaded. Please try again.";
        }
      } finally {
        if (requestId === this.projectionRequestId) this.projectionLoading = false;
      }
    },
    projectionTrend(current, previous) {
      const difference = Number((current - previous).toFixed(2));
      const direction = difference > 0 ? 1 : difference < 0 ? -1 : 0;
      const tone = direction > 0 ? "success" : direction < 0 ? "error" : null;
      const shade = this.$vuetify.theme.dark ? "lighten-3" : "darken-3";
      return {
        difference,
        direction,
        icon: direction > 0 ? "mdi-trending-up" : direction < 0 ? "mdi-trending-down" : "mdi-minus",
        label: direction > 0 ? "Above" : direction < 0 ? "Below" : "Matches",
        color: tone ? `${tone} ${shade}` : undefined,
        textClass: tone ? `${tone}--text text--${shade}` : "text--primary",
      };
    },
    projectionPace(location) {
      const { difference } = this.projectionTrend(location.currentRevenue, location.lastMonthSoFar);
      if (difference === 0) return "On pace with the same day last month.";
      const amount = this.$options.filters.currency(Math.abs(difference), location.currencySymbol);
      return `${difference > 0 ? "Ahead of" : "Behind"} the same day last month by ${amount}.`;
    },
  },
};
</script>

<style scoped>
.overview-toolbar, .overview-toolbar__controls, .overview-location-header, .overview-footnote { display: flex; align-items: center; justify-content: space-between; gap: 16px; }
.overview-period { width: 190px; }
.overview-metric { font-size: clamp(1.4rem, 2.1vw, 2rem); line-height: 1.4; overflow-wrap: anywhere; font-variant-numeric: tabular-nums; }
.overview-search { max-width: 300px; }
.overview-location-button { position: relative; display: block; width: 100%; color: inherit; text-align: left; }
.overview-location-button::before { position: absolute; inset: 0; content: ""; background: currentColor; opacity: 0; pointer-events: none; }
.overview-location-button:hover::before { opacity: .04; }
.overview-location-button:focus-visible { outline: 2px solid currentColor; outline-offset: -2px; }
.overview-location-button:focus-visible::before { opacity: .06; }
.overview-location-heading { display: grid; grid-template-columns: minmax(0, 1fr) auto; align-items: center; gap: 16px; }
.overview-location-name { overflow-wrap: anywhere; }
.overview-location-total { text-align: right; white-space: nowrap; font-variant-numeric: tabular-nums; }
.overview-location-breakdown { display: flex; flex-wrap: wrap; gap: 4px 20px; }
.overview-location-money { white-space: nowrap; font-variant-numeric: tabular-nums; }
.projection-header { display: grid; grid-template-columns: minmax(0, 1fr) auto; align-items: center; gap: 4px 16px; }
.projection-context { grid-column: 1 / -1; }
.projection-values { display: flex; flex-wrap: wrap; gap: 12px; }
.projection-values > div { flex: 1 1 130px; min-width: max-content; max-width: 100%; }
.projection-values .text-body-2, .projection-amount { white-space: nowrap; }
.projection-amount { font-size: 1.125rem; line-height: 1.5; font-variant-numeric: tabular-nums; }
.overview-footnote { align-items: flex-start; }
.overview-footnote > span:first-child { max-width: 740px; }
.overview-footnote > span:last-child { white-space: nowrap; }
.overview-match { font-weight: 700; text-decoration: underline; text-underline-offset: 3px; }
@media (max-width: 599px) {
  .overview-toolbar, .overview-location-header { align-items: stretch; flex-direction: column; }
  .overview-toolbar__controls { gap: 8px; }
  .overview-period, .overview-search { width: 100%; max-width: none; }
  .overview-footnote { flex-direction: column; gap: 8px; }
}
</style>
